import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus } from "lucide-react";

export default function Payments() {
  const qc = useQueryClient();
  const { data: payments } = useQuery({ queryKey: ["payments"], queryFn: () => api.get("/accounts/payments").then((r) => r.data) });
  const { data: outstanding } = useQuery({ queryKey: ["outstanding"], queryFn: () => api.get("/accounts/outstanding").then((r) => r.data) });
  const { data: customers } = useQuery({ queryKey: ["customers-mini"], queryFn: () => api.get("/customers?limit=200").then((r) => r.data) });
  const { data: invoices } = useQuery({ queryKey: ["invoices-mini"], queryFn: () => api.get("/accounts/invoices?limit=200").then((r) => r.data) });

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_id: "", invoice_id: "", amount: 0, method: "cash", reference: "", notes: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value });

  const create = useMutation({
    mutationFn: (payload) => api.post("/accounts/payments", payload),
    onSuccess: () => { toast.success("Payment recorded"); setOpen(false); setForm({ customer_id: "", invoice_id: "", amount: 0, method: "cash", reference: "", notes: "" }); qc.invalidateQueries(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed"),
  });

  const custInvoices = (invoices?.items || []).filter((i) => i.customer_id === form.customer_id && i.payment_status !== "paid");

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight">Payments & Collections</h1>
          <p className="text-sm text-muted-foreground">₹{(outstanding?.total_outstanding || 0).toLocaleString("en-IN")} outstanding across {outstanding?.items?.length || 0} customers</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="btn-new-payment"><Plus size={14} className="mr-1" /> Record payment</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Record payment</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Customer</Label>
                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v, invoice_id: "" })}>
                  <SelectTrigger data-testid="payment-customer"><SelectValue placeholder="Pick customer" /></SelectTrigger>
                  <SelectContent>{(customers?.items || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.code} · {c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Against invoice (optional)</Label>
                <Select value={form.invoice_id} onValueChange={(v) => setForm({ ...form, invoice_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="payment-invoice"><SelectValue placeholder="On-account" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">On-account</SelectItem>
                    {custInvoices.map((i) => <SelectItem key={i.id} value={i.id}>{i.code} · ₹{i.total - (i.paid_amount || 0)} due</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Amount</Label><Input type="number" value={form.amount} onChange={set("amount")} data-testid="payment-amount" /></div>
              <div>
                <Label>Method</Label>
                <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                  <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank">Bank Transfer</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="wallet">Wallet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Reference (Txn ID, cheque, UPI ID)</Label><Input value={form.reference} onChange={set("reference")} /></div>
            </div>
            <DialogFooter><Button onClick={() => create.mutate(form)} disabled={!form.customer_id || !form.amount} data-testid="btn-save-payment">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>Top outstanding</CardTitle></CardHeader>
          <CardContent className="p-0">
            <table className="data-table w-full">
              <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
                <tr><th className="px-4 py-2">Customer</th><th className="px-4 py-2 text-right">Outstanding</th><th className="px-4 py-2 text-right">Limit</th></tr>
              </thead>
              <tbody>
                {(outstanding?.items || []).slice(0, 10).map((c) => (
                  <tr key={c.id} className="border-b border-border/40 text-sm">
                    <td className="px-4 py-2 font-semibold">{c.name}<div className="text-xs text-muted-foreground">{c.code} · {c.phone}</div></td>
                    <td className="px-4 py-2 text-right font-mono font-bold text-primary">₹{c.outstanding.toLocaleString("en-IN")}</td>
                    <td className="px-4 py-2 text-right font-mono text-muted-foreground">₹{(c.credit_limit || 0).toLocaleString("en-IN")}</td>
                  </tr>
                ))}
                {(!outstanding?.items || outstanding.items.length === 0) && (<tr><td colSpan={3} className="px-4 py-6 text-center text-sm text-muted-foreground">All settled 👏</td></tr>)}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle>Recent payments</CardTitle></CardHeader>
          <CardContent className="p-0">
            <table className="data-table w-full">
              <thead className="border-b border-border/60 bg-muted/40 text-left text-xs uppercase tracking-widest text-muted-foreground">
                <tr><th className="px-4 py-2">Code</th><th className="px-4 py-2">Customer</th><th className="px-4 py-2">Method</th><th className="px-4 py-2 text-right">Amount</th></tr>
              </thead>
              <tbody>
                {(payments?.items || []).slice(0, 15).map((p) => (
                  <tr key={p.id} className="border-b border-border/40 text-sm">
                    <td className="px-4 py-2 font-mono">{p.code}</td>
                    <td className="px-4 py-2">{p.customer_name}</td>
                    <td className="px-4 py-2 text-xs uppercase">{p.method}</td>
                    <td className="px-4 py-2 text-right font-mono font-semibold">₹{p.amount.toLocaleString("en-IN")}</td>
                  </tr>
                ))}
                {(!payments?.items || payments.items.length === 0) && (<tr><td colSpan={4} className="px-4 py-6 text-center text-sm text-muted-foreground">No payments yet</td></tr>)}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
